import { useState } from "react";
import { useTest } from "../context/TestContext";

function Question({ reviewMode, testResult }) {
  const { questions, currentIndex, answers, setAnswers } = useTest();
  const [hoveredOption, setHoveredOption] = useState(null);

  const q = questions[currentIndex];

  if (!q) return null;

  const questionType = q.question_type || "single_choice";

  // console.log(`=== Question Render ===`);
  // console.log(`Current index: ${currentIndex}`);
  // console.log(`Question data:`, q);
  // console.log(`Question type: ${questionType}`);
  // console.log(`Correct answer field:`, q.correct_answer);
  // console.log(`Correct answers field:`, q.correct_answers);
  // console.log(`Options:`, q.options);

  const isMultiSelect = questionType === "multi_select";
  const maxSelections = q.maxSelections || 1;

  const getSelectedOptions = () => {
    const answer = answers[currentIndex];
    if (Array.isArray(answer)) {
      return answer;
    }
    return answer ? [answer] : [];
  };

  const handleOptionSelect = (optionKey) => {
    if (reviewMode) return;

    const selectedOptions = getSelectedOptions();
    const questionType = q.question_type || "single_choice";

    // console.log(`=== handleOptionSelect called ===`);
    // console.log(`Current index: ${currentIndex}`);
    // console.log(`Option selected: ${optionKey}`);
    // console.log(`Question type: ${questionType}`);
    // console.log(`Current answers before:`, answers);

    if (isMultiSelect) {
      let newSelections;
      if (selectedOptions.includes(optionKey)) {
        newSelections = selectedOptions.filter(opt => opt !== optionKey);
      } else if (selectedOptions.length < maxSelections) {
        newSelections = [...selectedOptions, optionKey];
      } else {
        return;
      }
      // console.log(`New multi-select answers:`, newSelections);
      setAnswers({ ...answers, [currentIndex]: newSelections });
    } else {
      if (answers[currentIndex] === optionKey) {
        const newAnswers = { ...answers };
        delete newAnswers[currentIndex];
        // console.log(`Deselecting answer for index ${currentIndex}`);
        setAnswers(newAnswers);
      } else {
        // console.log(`Setting answer for index ${currentIndex} to "${optionKey}"`);
        setAnswers({ ...answers, [currentIndex]: optionKey });
      }
    }

    // console.log(`Answers will be updated to:`, { ...answers, [currentIndex]: optionKey });
  };

  const isOptionCorrect = (optionKey) => {
    const questionType = q.question_type || "single_choice";

    if (questionType === "single_choice") {
      const correctAnswer = q.correct_answer !== undefined ? q.correct_answer : q.correct_answers;
      const normalizedOption = String(optionKey).trim();
      const normalizedCorrect = String(correctAnswer).trim();
      return normalizedOption === normalizedCorrect;
    } else if (questionType === "multi_select") {
      let correctAnswers = q.correct_answers || [];
      if (!Array.isArray(correctAnswers)) {
        if (typeof correctAnswers === 'string') {
          correctAnswers = correctAnswers.split(',').map(s => s.trim()).filter(s => s);
        } else {
          correctAnswers = [];
        }
      }
      return correctAnswers.map(String).map(s => s.trim()).includes(String(optionKey).trim());
    }
    return false;
  };

  const isUserSelectedCorrect = (optionKey) => {
    const selectedOptions = getSelectedOptions();
    if (!selectedOptions.includes(optionKey)) return false;
    return isOptionCorrect(optionKey);
  };

  const isUserSelectedWrong = (optionKey) => {
    const selectedOptions = getSelectedOptions();
    if (!selectedOptions.includes(optionKey)) return false;
    return !isOptionCorrect(optionKey);
  };

  const selectedOptions = getSelectedOptions();

  const renderOption = (k, v, isSelected) => {
    let backgroundColor = isSelected ? "#007bff" : "transparent";
    let borderColor = isSelected ? "2px solid #007bff" : "1px solid #ccc";
    let textColor = isSelected ? "white" : "#333";
    let icon = null;

    if (reviewMode) {
      const isCorrect = isOptionCorrect(k);
      const isUserSelected = selectedOptions.includes(k);
      const isUserWrong = isUserSelected && !isCorrect;
      const isUserCorrect = isUserSelected && isCorrect;

      if (isUserCorrect) {
        backgroundColor = "#d4edda";
        borderColor = "2px solid #28a745";
        textColor = "#155724";
        icon = "✅";
      } else if (isUserWrong) {
        backgroundColor = "#f8d7da";
        borderColor = "2px solid #dc3545";
        textColor = "#721c24";
        icon = "❌";
      } else if (isCorrect) {
        backgroundColor = "#d4edda";
        borderColor = "2px solid #28a745";
        textColor = "#155724";
        icon = null;
      } else {
        backgroundColor = "transparent";
        borderColor = "1px solid #ccc";
        textColor = "#333";
      }
    } else if (hoveredOption === k && !isSelected) {
      backgroundColor = "#F1F5F9";
    }

    return (
      <button
        key={k}
        onClick={() => handleOptionSelect(k)}
        onMouseEnter={() => {
          if (!isSelected && !reviewMode) {
            setHoveredOption(k);
          }
        }}
        onMouseLeave={() => {
          if (!isSelected && !reviewMode) {
            setHoveredOption(null);
          }
        }}
        style={{
          padding: "6px 6px",
          textAlign: "left",
          background: backgroundColor,
          color: textColor,
          border: borderColor,
          borderRadius: 8,
          cursor: reviewMode ? "default" : "pointer",
          fontSize: "14px",
          transition: "background 0.2s",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px"
        }}
      >
        <span><strong>{k}:</strong> {v}</span>
        {icon && <span>{icon}</span>}
      </button>
    );
  };

  return (
    <div style={{ filter: "blur(0px)" }}>
      <p style={{ fontSize: "14px", color: "#666", marginBottom: 10 }}>
        Question <strong>{currentIndex + 1}/{questions.length}</strong>
      </p>
      <h3 style={{ fontSize: "16px", marginBottom: 20 }}>{q.question}</h3>

      {isMultiSelect && !reviewMode && (
        <p style={{ fontSize: "14px", color: "#666", marginBottom: 10 }}>
          Select up to {maxSelections} option{maxSelections > 1 ? "s" : ""} (currently {selectedOptions.length} selected)
        </p>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {Object.entries(q.options)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([k, v]) => {
            const isSelected = selectedOptions.includes(k);
            return renderOption(k, v, isSelected);
          })}
      </div>

      {reviewMode && (
        <div style={{ marginTop: 20, padding: "15px", backgroundColor: "#F1F5F9", borderRadius: 8, border: "1px solid #e0e0e0" }}>
          {q.domain && (
            <p style={{ fontSize: "14px", margin: "0 0 5px 0" }}>
              <strong>Domain:</strong> {q.domain}
            </p>
          )}
          {q.task && (
            <p style={{ fontSize: "14px", margin: "0 0 5px 0" }}>
              <strong>Task:</strong> {q.task}
            </p>
          )}
          {q.explanation && (
            <p style={{ fontSize: "14px", margin: "0 0 5px 0", color: "#495057" }}>
              <strong>Explanation:</strong> {q.explanation}
            </p>
          )}
          {q.reference && (
            <p style={{ fontSize: "14px", margin: "0", color: "#666" }}>
              <strong>Reference:</strong> {q.reference}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default Question;
